import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Loader2, Cloud, Check, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const DROPBOX_APP_KEY = process.env.REACT_APP_DROPBOX_APP_KEY;

export const DropboxChooser = ({ onFilesSelected, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDropboxLoaded, setIsDropboxLoaded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isImporting, setIsImporting] = useState(false);

  // Load Dropbox Chooser script
  useEffect(() => {
    if (window.Dropbox) {
      setIsDropboxLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
    script.id = 'dropboxjs';
    script.setAttribute('data-app-key', DROPBOX_APP_KEY || '');
    script.async = true;
    
    script.onload = () => {
      setIsDropboxLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load Dropbox script');
      toast.error('Failed to load Dropbox integration');
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Open Dropbox Chooser
  const openChooser = useCallback(() => {
    if (!window.Dropbox) {
      toast.error('Dropbox is not loaded yet');
      return;
    }

    if (!DROPBOX_APP_KEY) {
      toast.error('Dropbox App Key is not configured');
      return;
    }

    setIsLoading(true);

    window.Dropbox.choose({
      success: (files) => {
        const mappedFiles = files.map(file => ({
          id: file.id || file.link,
          name: file.name,
          link: file.link,
          bytes: file.bytes,
          icon: file.icon,
          thumbnailLink: file.thumbnailLink,
          isDir: file.isDir,
        }));
        setSelectedFiles(mappedFiles);
        setIsLoading(false);
      },
      cancel: () => {
        setIsLoading(false);
      },
      linkType: 'direct', // Direct download links
      multiselect: true,
      extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'],
      folderselect: false,
    });
  }, []);

  // Import selected files
  const handleImport = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files first');
      return;
    }

    setIsImporting(true);
    
    try {
      await onFilesSelected(selectedFiles);
      toast.success(`${selectedFiles.length} image(s) imported successfully!`);
      setSelectedFiles([]);
      onClose?.();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import files: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const isConfigured = !!DROPBOX_APP_KEY;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#0061FF">
            <path d="M6 2L12 6L6 10L0 6L6 2Z"/>
            <path d="M18 2L24 6L18 10L12 6L18 2Z"/>
            <path d="M0 14L6 10L12 14L6 18L0 14Z"/>
            <path d="M18 10L24 14L18 18L12 14L18 10Z"/>
            <path d="M6 19L12 15L18 19L12 23L6 19Z"/>
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-blue-900">Dropbox</h3>
          <p className="text-sm text-blue-700">
            {isConfigured ? 'Select images from your Dropbox' : 'Not configured yet'}
          </p>
        </div>
        <Button
          onClick={openChooser}
          disabled={!isDropboxLoaded || isLoading || !isConfigured}
          className="bg-[#0061FF] hover:bg-[#004FC4] text-white"
          data-testid="dropbox-chooser-btn"
        >
          {isLoading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
          ) : !isDropboxLoaded ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Initializing...</>
          ) : !isConfigured ? (
            <>Coming Soon</>
          ) : (
            <><Cloud className="w-4 h-4 mr-2" />Browse Dropbox</>
          )}
        </Button>
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-[#1E3A5F]">
              Selected Files ({selectedFiles.length})
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFiles([])}
              className="text-red-500 hover:text-red-700"
            >
              Clear All
            </Button>
          </div>
          
          <div className="max-h-60 overflow-y-auto space-y-2">
            {selectedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center overflow-hidden">
                  {file.thumbnailLink ? (
                    <img src={file.thumbnailLink} alt="" className="w-full h-full object-cover" />
                  ) : file.icon ? (
                    <img src={file.icon} alt="" className="w-6 h-6" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {file.bytes ? `${(file.bytes / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(file.id)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          <Button
            onClick={handleImport}
            disabled={isImporting}
            className="w-full bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white"
            data-testid="dropbox-import-btn"
          >
            {isImporting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
            ) : (
              <><Check className="w-4 h-4 mr-2" />Import {selectedFiles.length} Image(s)</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default DropboxChooser;
